import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { supabase } from '../lib/supabase.js';
import {
  SignalFlagsSchema,
  FeedbackAttachmentSchema,
  type BuildMarker,
  type GameDevStage,
  type SentimentBand,
} from '@playerbase/types';
import {
  buildChangesWithin,
  type BuildMarkerSummary,
} from '../games/build-timeline.js';
import {
  fetchFeedbackInboxSubmissions,
  fetchFeedbackFormSubmissionById,
  fetchFormSubmissionSentiments,
  INBOX_SUBMISSION_PAGE_SIZE,
  type EnrichedFeedbackFormSubmission,
  type FormSubmissionEngagement,
  type FormSubmissionFieldSummary,
} from '../games/feedback-forms-data.js';
import {
  aggregateReactions,
  ReactionEventSchema,
  type ReactionEvent,
  type ReactionTotal,
} from './reaction-aggregate.js';

// ---------------------------------------------------------------------------
// Feedback inbox data hook.
//
// Reads feedback_pool rows for the active game with sibling poll +
// reaction history embedded via PostgREST's relationship syntax. RLS
// (migration 0002) handles studio scoping; the explicit eq('game_id')
// is a UX scope, not a security boundary.
//
// Activity-log ordering is data-derived: each bundle's lastActivityAt
// is max(message.posted_at, message.edited_at, max(reaction.observed_at))
// across every message in the bundle. The feed sorts by that
// descending so a reaction on an old bundle bumps the bundle to the
// top and the order survives a page reload (the answer comes straight
// from the join). The reaction with the latest 'add' is flagged
// `isLatest` so the row component can border-highlight whichever
// emoji "made" the bundle recent.
//
// Bundling: consecutive messages from the same author in the same
// channel/thread, within a 5-minute window, sharing the same
// reply-target and with no poll on either side, render as one row
// with stacked bodies. This matches Slack/Discord feel - quick-fire
// follow-ups read as "one thought" without N rows of repeated
// avatar/timestamp chrome.
//
// Pagination cursor stays on posted_at - we still load older messages
// chronologically. lastActivityAt only governs the visual order of
// already-loaded rows.
//
// Grouping (PR 2): once bundles exist, group them. Forum threads
// (rows sharing a non-null thread_id) collapse into one InboxGroup
// with the thread starter as the head and other bundles as replies.
// Reply chains in regular channels nest replies under their on-page
// parent; reply-of-reply chains flatten to the topmost ancestor.
// Anything else passes through as kind: 'single'. The feedback-inbox
// container renders InboxGroup[] rather than InboxRow[].
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

// Activity panel only surfaces items whose latest activity is within
// this window. Older items still appear in the chronological feed.
const ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Two messages from the same author bundle when their posted_at
// timestamps differ by at most this window. 5 minutes is the
// Slack/Discord default and matches the "did they pause to write or
// keep going?" intuition for studio-side reading.
const MESSAGE_BUNDLE_WINDOW_MS = 5 * 60 * 1000;

const PollAnswerSchema = z.object({
  id: z.number().int(),
  text: z.string().nullable(),
  emoji: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
      animated: z.boolean().nullable(),
    })
    .nullable(),
  voteCount: z.number().int().nonnegative(),
});

const RowSchema = z.object({
  id: z.string().uuid(),
  author_handle: z.string().nullable(),
  author_external_id: z.string().nullable(),
  raw_text: z.string(),
  posted_at: z.string().nullable(),
  edited_at: z.string().nullable(),
  signal_flags: SignalFlagsSchema,
  attachments: z.array(FeedbackAttachmentSchema),
  discord_channel_id: z.string().nullable(),
  discord_channel_name: z.string().nullable(),
  discord_parent_channel_id: z.string().nullable(),
  discord_parent_channel_name: z.string().nullable(),
  thread_id: z.string().nullable(),
  reply_to_external_id: z.string().nullable(),
  external_id: z.string().nullable(),
  // Embedded sibling rows. PostgREST returns these as arrays even for
  // 1:1 relationships; feedback_poll has a unique(feedback_id) so the
  // array has 0 or 1 element.
  // PostgREST detects feedback_poll as a 1:1 relationship via the
  // unique(feedback_id) constraint and returns the embed as a plain
  // object (or null), not a single-element array. (feedback_reaction
  // below stays an array because it's 1:N.)
  feedback_poll: z
    .object({
      id: z.string().uuid(),
      question: z.string(),
      answers: z.array(PollAnswerSchema),
      finalised: z.boolean().optional().default(false),
      expires_at: z.string().nullable().optional(),
      updated_at: z.string().optional(),
      allow_multiselect: z.boolean().optional().default(false),
      // Per-vote events. We need answer_id + voter + action to
       // compute the live tally client-side; observed_at drives the
       // activity-panel ordering.
      feedback_poll_vote: z
        .array(
          z.object({
            observed_at: z.string(),
            answer_id: z.number().int(),
            voter_external_id: z.string(),
            action: z.enum(['add', 'remove']),
          }),
        )
        .nullable()
        .optional()
        .default([]),
    })
    .nullable(),
  feedback_reaction: z.array(ReactionEventSchema).nullable(),
});

const ReactionPayloadSchema = z.object({ feedback_id: z.string().uuid() });
const PollVotePayloadSchema = z.object({ poll_id: z.string().uuid() });
const PollUpdatePayloadSchema = z.object({ feedback_id: z.string().uuid() });
const FormSubmissionPayloadSchema = z.object({ id: z.string().uuid() });
// Subset of the feedback_pool INSERT payload we need to detect whether a
// new reply / thread message attaches to a form submission's summary
// post. The realtime payload always carries every column on INSERT, but
// the schema stays narrow so an unrelated row shape doesn't trip parse.
const FeedbackPoolInsertPayloadSchema = z.object({
  source: z.string().nullable().optional(),
  reply_to_external_id: z.string().nullable().optional(),
  thread_id: z.string().nullable().optional(),
});

type PollAnswer = z.infer<typeof PollAnswerSchema>;

export type { ReactionTotal };

export interface InboxMessage {
  id: string;
  externalId: string | null;
  authorHandle: string | null;
  authorExternalId: string | null;
  rawText: string;
  postedAt: string | null;
  editedAt: string | null;
  signalFlags: z.infer<typeof SignalFlagsSchema>;
  attachments: z.infer<typeof FeedbackAttachmentSchema>[];
  discordChannelId: string | null;
  discordChannelName: string | null;
  discordParentChannelId: string | null;
  discordParentChannelName: string | null;
  threadId: string | null;
  replyToExternalId: string | null;
  pollId: string | null;
  poll: {
    question: string;
    answers: PollAnswer[];
    finalised: boolean;
    expiresAt: string | null;
    updatedAt: string;
    allowMultiselect: boolean;
    totalVotes: number;
    // Max(observed_at) across feedback_poll_vote events for this
    // poll. Null when there have been no votes yet. Drives both the
    // activity-ordering bump and the activity-panel "vote on poll"
    // label detection.
    lastVoteAt: string | null;
    // Snapshot of the most-recent voter's full state on this poll
    // (the set of answers they currently have an active 'add' for).
    // For multi-select polls this can carry several answers; for
    // single-select it carries at most one. Null when no vote
    // events exist.
    lastVote: {
      voterExternalId: string;
      action: 'add' | 'remove';
      choices: Array<{
        answerId: number;
        text: string | null;
        emoji: {
          id: string | null;
          name: string | null;
          animated: boolean | null;
        } | null;
      }>;
    } | null;
  } | null;
  // Raw reaction events for this message; aggregated at the bundle
  // level so the bundle exposes one combined reaction set.
  reactionEvents: ReactionEvent[];
}

export interface InboxRow {
  // Stable bundle id derived from the first message - so React keys
  // don't churn when a follow-up message extends the bundle.
  id: string;
  authorHandle: string | null;
  authorExternalId: string | null;
  discordChannelId: string | null;
  discordChannelName: string | null;
  discordParentChannelName: string | null;
  threadId: string | null;
  replyToExternalId: string | null;
  // Messages in chronological order (oldest first within the bundle).
  messages: InboxMessage[];
  // Aggregated reactions across the bundle.
  reactions: ReactionTotal[];
  // Sort key for activity-log ordering. Derived; never persisted.
  lastActivityAt: string;
  // Poll status - derived from the bundle's first poll-bearing
  // message, if any. Bundles never carry more than one poll because
  // shouldBundle bails on poll presence. Null when no poll on this
  // row.
  pollStatus: {
    pollId: string;
    finalised: boolean;
    expiresAt: string | null;
    totalVotes: number;
    // Most recent timestamp linked to the poll (max of poll.updatedAt
    // and lastVoteAt). Drives the activity-panel "poll closed" /
    // "vote on poll" label detection.
    lastEventAt: string | null;
    // Snapshot of the most-recent voter's full state on this poll.
    // Carries every answer they currently have an active 'add' for
    // (one for single-select, possibly several for multi-select).
    // Null when no vote events exist.
    lastVote: {
      voterExternalId: string;
      action: 'add' | 'remove';
      choices: Array<{
        answerId: number;
        text: string | null;
        emoji: {
          id: string | null;
          name: string | null;
          animated: boolean | null;
        } | null;
      }>;
    } | null;
  } | null;
}

// Top-level unit of the feed.
//   - 'forum-post' : a thread inside a forum channel. The starter
//     post is itself a thread message; renders as a clickable card
//     that expands into the full conversation.
//   - 'thread'     : a thread spawned off a chat message. The parent
//     is a regular chat message; we nest the replies inline beneath
//     it so the conversation stays visible in the feed.
//   - 'reply-chain': a reply chain in a regular channel (no thread).
//     Replies nest one level under their on-page parent.
//   - 'single'     : a single bundle, no thread, no replies.
export type GroupKind = 'forum-post' | 'thread' | 'reply-chain' | 'single';

export interface InboxGroup {
  id: string;
  kind: GroupKind;
  head: InboxRow;
  // Chronological (oldest -> newest). Empty for kind: 'single'.
  replies: InboxRow[];
  // Set iff kind === 'thread'.
  threadId: string | null;
  // Max lastActivityAt across head + every reply. Drives the activity
  // panel's ordering; the main feed sorts by chronologicalAt instead.
  lastActivityAt: string;
  // Original posted_at of the group's head row. Immutable - reactions /
  // replies don't move this. The main feed sorts by this DESC so the
  // chronology of the conversation stays readable.
  chronologicalAt: string;
  // True iff the group has activity beyond its own creation event AND
  // that activity is within ACTIVITY_WINDOW_MS. Drives membership in
  // the right-hand activity panel; a freshly posted message with no
  // interactions has lastActivityAt === chronologicalAt and stays out.
  hasRecentActivity: boolean;
  // Thread-only: true iff the actual thread starter
  // (external_id == thread_id) hasn't been loaded yet and head is just
  // the oldest on-page reply. Renders an "earlier messages in this
  // thread" hint above the head and triggers an on-demand fetch.
  starterMissing: boolean;
}

// Form-submission entries surface alongside chronological message groups
// in the inbox. They share none of the bundling / threading machinery
// (each submission is its own atomic event), so we keep them as their own
// type rather than shoehorning into InboxGroup.
export interface FormSubmissionEntry {
  id: string;
  formId: string;
  // Sibling feedback_pool row id. Used by the reaction realtime
  // subscription to detect when an incoming reaction targets this
  // submission's summary embed.
  feedbackPoolId: string | null;
  formTitle: string;
  surveyChannelId: string | null;
  submitterHandle: string | null;
  submitterExternalId: string | null;
  submittedAt: string;
  // Field id -> string|number map. The card iterates the form's field
  // list (below) for ordering and to recover the question text.
  values: Record<string, string | number>;
  // Field metadata snapshot at fetch time. Pulled via the same join so
  // the submission card renders without a second round trip. Carries
  // optional kind + config from the current field row so the card can
  // resolve canonical values (binary's true/false) to the studio's
  // chosen labels.
  fields: FormSubmissionFieldSummary[];
  // Discord engagement on the summary embed (reactions / replies / thread
  // messages). Null when the submission hasn't been posted to Discord or
  // the writeback hasn't run.
  engagement: FormSubmissionEngagement | null;
  // Max(submittedAt, latest reaction/reply timestamp). Drives the
  // activity-feed sort key so an engaged submission bumps to the top
  // the same way a reacted-to message group does.
  lastActivityAt: string;
  // True iff engagement has happened since submittedAt within the
  // activity window. Mirrors InboxGroup.hasRecentActivity so the
  // activity panel applies the same membership rule to both kinds.
  hasRecentActivity: boolean;
  // Derived overall-sentiment band for this submission (migration 0106),
  // fetched per page via get_form_submission_sentiments and merged by id.
  // null when no sentiment has been derived yet (no enrichment, no rating
  // fields) - the card renders no dot in that case.
  sentimentBand: SentimentBand | null;
}

// Heterogeneous activity-feed entry. The activity panel renders one of
// these per row; the discriminator routes between the existing
// InboxGroup-driven items and the new form-submission tile.
export type ActivityEntry =
  | { kind: 'group'; group: InboxGroup }
  | { kind: 'form_submission'; submission: FormSubmissionEntry }
  // A build going live. Game-wide (not channel-scoped), so these surface
  // regardless of the active channel chip. `changedAt` is the marker's
  // effective time (released_at, falling back to created_at) and drives
  // both the activity-feed sort and the in-feed divider position (PR3).
  | { kind: 'build_marker'; marker: BuildMarkerSummary; changedAt: string };

function parseMessage(raw: z.infer<typeof RowSchema>): InboxMessage {
  const poll = raw.feedback_poll ?? null;
  let pollDetail: InboxMessage['poll'] = null;
  if (poll !== null) {
    // Live vote tally from the feedback_poll_vote event log:
    // for each (voter, answer) pair the latest action wins, then we
    // count the 'add' survivors per answer_id. This is authoritative
    // for any vote that happened while the bot was running.
    //
    // The snapshot on answers[].voteCount can hold pre-bot votes
    // that the event log doesn't have, so we display the per-answer
    // max of (snapshot, live) - never undercounting either source.
    const votes = poll.feedback_poll_vote ?? [];
    const sortedVotes = [...votes].sort((a, b) =>
      a.observed_at.localeCompare(b.observed_at),
    );
    // Map<answerId, Map<voterId, latestAction>>.
    const latestByVoter = new Map<number, Map<string, 'add' | 'remove'>>();
    let lastVoteAt: string | null = null;
    for (const v of sortedVotes) {
      let inner = latestByVoter.get(v.answer_id);
      if (inner === undefined) {
        inner = new Map();
        latestByVoter.set(v.answer_id, inner);
      }
      inner.set(v.voter_external_id, v.action);
      if (lastVoteAt === null || v.observed_at > lastVoteAt) lastVoteAt = v.observed_at;
    }
    const liveCountByAnswer = new Map<number, number>();
    for (const [answerId, voters] of latestByVoter) {
      let count = 0;
      for (const action of voters.values()) if (action === 'add') count += 1;
      liveCountByAnswer.set(answerId, count);
    }

    const reconciledAnswers = poll.answers.map((a) => {
      const live = liveCountByAnswer.get(a.id) ?? 0;
      return { ...a, voteCount: Math.max(a.voteCount, live) };
    });
    const totalVotes = reconciledAnswers.reduce((sum, a) => sum + a.voteCount, 0);

    // Snapshot the most-recent voter's current state. For multi-
    // select polls one voter may have several active 'add' rows;
    // we collect them all so the activity panel can list every
    // answer they're currently carrying. For 'remove' events with
    // no active adds left, choices is an empty array.
    let lastVote: NonNullable<InboxMessage['poll']>['lastVote'] = null;
    if (sortedVotes.length > 0) {
      const latest = sortedVotes[sortedVotes.length - 1]!;
      const voterId = latest.voter_external_id;
      // Walk the events again, but only for this voter, so the
      // map of answer_id -> latestAction reflects their state.
      const voterState = new Map<number, 'add' | 'remove'>();
      for (const v of sortedVotes) {
        if (v.voter_external_id !== voterId) continue;
        voterState.set(v.answer_id, v.action);
      }
      const choices = [];
      for (const [answerId, action] of voterState) {
        if (action !== 'add') continue;
        const a = poll.answers.find((x) => x.id === answerId) ?? null;
        choices.push({
          answerId,
          text: a?.text ?? null,
          emoji: a?.emoji ?? null,
        });
      }
      lastVote = {
        voterExternalId: voterId,
        action: latest.action,
        choices,
      };
    }

    pollDetail = {
      question: poll.question,
      answers: reconciledAnswers,
      finalised: poll.finalised,
      expiresAt: poll.expires_at ?? null,
      updatedAt: poll.updated_at ?? '',
      allowMultiselect: poll.allow_multiselect,
      totalVotes,
      lastVoteAt,
      lastVote,
    };
  }
  return {
    id: raw.id,
    externalId: raw.external_id,
    authorHandle: raw.author_handle,
    authorExternalId: raw.author_external_id,
    rawText: raw.raw_text,
    postedAt: raw.posted_at,
    editedAt: raw.edited_at,
    signalFlags: raw.signal_flags,
    attachments: raw.attachments,
    discordChannelId: raw.discord_channel_id,
    discordChannelName: raw.discord_channel_name,
    discordParentChannelId: raw.discord_parent_channel_id,
    discordParentChannelName: raw.discord_parent_channel_name,
    threadId: raw.thread_id,
    replyToExternalId: raw.reply_to_external_id,
    pollId: poll?.id ?? null,
    poll: pollDetail,
    reactionEvents: raw.feedback_reaction ?? [],
  };
}

// Two messages bundle iff they share enough metadata that a reader
// would treat them as one logical post. Same author, same channel /
// thread, same reply target, no poll (a poll is its own thing), and
// posted within MESSAGE_BUNDLE_WINDOW_MS of each other.
function shouldBundle(prev: InboxMessage, next: InboxMessage): boolean {
  if (prev.authorExternalId === null || next.authorExternalId === null) return false;
  if (prev.authorExternalId !== next.authorExternalId) return false;
  if (prev.discordChannelId !== next.discordChannelId) return false;
  if (prev.threadId !== next.threadId) return false;
  if (prev.replyToExternalId !== next.replyToExternalId) return false;
  if (prev.poll !== null || next.poll !== null) return false;
  if (prev.postedAt === null || next.postedAt === null) return false;
  const dt = new Date(next.postedAt).getTime() - new Date(prev.postedAt).getTime();
  return dt >= 0 && dt <= MESSAGE_BUNDLE_WINDOW_MS;
}

function bundleToRow(bundle: InboxMessage[]): InboxRow {
  const head = bundle[0]!;
  const allReactionEvents = bundle.flatMap((m) => m.reactionEvents);
  const aggregate = aggregateReactions(allReactionEvents);

  let lastActivityAt = '';
  for (const m of bundle) {
    if (m.postedAt !== null && m.postedAt > lastActivityAt) lastActivityAt = m.postedAt;
    if (m.editedAt !== null && m.editedAt > lastActivityAt) lastActivityAt = m.editedAt;
  }
  for (const e of allReactionEvents) {
    if (e.observed_at > lastActivityAt) lastActivityAt = e.observed_at;
  }

  // Polls don't bundle (shouldBundle bails on either side carrying a
  // poll), so a row has at most one poll-bearing message - and that
  // message is necessarily the head. Compute the poll's last-event
  // timestamp from the carried fields and fold it into the bundle's
  // lastActivityAt so vote / finalisation arrivals bump the row in
  // the activity panel just like reactions do for messages.
  const pollMessage = bundle.find((m) => m.poll !== null);
  let pollStatus: InboxRow['pollStatus'] = null;
  if (pollMessage !== undefined && pollMessage.poll !== null && pollMessage.pollId !== null) {
    const poll = pollMessage.poll;
    // The poll's last *event* time - used to bump the activity panel and
    // (in pollEventKind) to label created / voted / closed. Built from real
    // event times only: creation (the message posted_at), the latest vote
    // (its observed_at via lastVoteAt), and, once closed, the poll's
    // scheduled expiry. Never feedback_poll.updated_at: that is the bot's
    // write time, so a backfill (written now, but created / closed weeks
    // ago) stamped the poll "now" and floated it into the recent-activity
    // feed even though nothing recent happened.
    let lastEventAt: string | null = pollMessage.postedAt;
    if (poll.lastVoteAt !== null && (lastEventAt === null || poll.lastVoteAt > lastEventAt)) {
      lastEventAt = poll.lastVoteAt;
    }
    if (
      poll.finalised &&
      poll.expiresAt !== null &&
      (lastEventAt === null || poll.expiresAt > lastEventAt)
    ) {
      lastEventAt = poll.expiresAt;
    }
    if (lastEventAt !== null && lastEventAt > lastActivityAt) lastActivityAt = lastEventAt;
    pollStatus = {
      pollId: pollMessage.pollId,
      finalised: poll.finalised,
      expiresAt: poll.expiresAt,
      totalVotes: poll.totalVotes,
      lastEventAt,
      lastVote: poll.lastVote,
    };
  }

  // Only mark the latest pill when the bundle's lastActivity actually
  // came from a reaction. If a new message inside the bundle is the
  // most recent thing, no pill should pop.
  if (
    aggregate.latestAddObservedAt === null ||
    aggregate.latestAddObservedAt < lastActivityAt
  ) {
    for (const t of aggregate.totals) t.isLatest = false;
  }

  return {
    id: head.id,
    authorHandle: head.authorHandle,
    authorExternalId: head.authorExternalId,
    discordChannelId: head.discordChannelId,
    discordChannelName: head.discordChannelName,
    discordParentChannelName: head.discordParentChannelName,
    threadId: head.threadId,
    replyToExternalId: head.replyToExternalId,
    messages: bundle,
    reactions: aggregate.totals,
    lastActivityAt,
    pollStatus,
  };
}

function bundleMessages(messages: InboxMessage[]): InboxRow[] {
  // Drop signalFlags.isEmpty messages before bundling - they are
  // image-only or empty-content edges we don't want as feed entries
  // and shouldn't break adjacency between two real messages.
  const visible = messages.filter((m) => m.signalFlags.isEmpty !== true);
  const sorted = [...visible].sort((a, b) =>
    (a.postedAt ?? '').localeCompare(b.postedAt ?? ''),
  );

  const bundles: InboxMessage[][] = [];
  let current: InboxMessage[] = [];
  for (const m of sorted) {
    if (current.length === 0) {
      current.push(m);
      continue;
    }
    const prev = current[current.length - 1]!;
    if (shouldBundle(prev, m)) {
      current.push(m);
    } else {
      bundles.push(current);
      current = [m];
    }
  }
  if (current.length > 0) bundles.push(current);

  return bundles
    .map(bundleToRow)
    .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
}

// Two-pass grouping over already-bundled rows.
//
// Pass 1 (threads): bucket every row with a non-null threadId by
// threadId. The thread starter is the bundle whose first message has
// externalId == threadId. Discord uses one snowflake for the thread,
// the thread's channel, and the starter message - which means:
//  - For forum threads, the starter sits inside the bucket itself.
//  - For threads spawned off a chat message, the starter is a row in
//    a different channel with thread_id = null and external_id =
//    threadId. We pull it in from the global externalId index and
//    nest the thread bundles under it.
// If no on-page bundle satisfies either case, fall back to the
// oldest in-bucket bundle and flag starterMissing - the actual
// starter sits below the page cursor and the hook will try to pull
// it on demand.
//
// Pass 2 (reply chains): of the rows that didn't end up in a thread
// group, attach any row with a non-null replyToExternalId to its
// parent's group when the parent is on-page. Reply-of-reply chains
// flatten to the topmost on-page ancestor (v1 keeps a flat replies
// list; no nested trees).
//
// Anything that didn't become a head or a reply is kind: 'single'.
//
// The hook returns two derived top-level lists from the same group set:
//   - feed groups, sorted by chronologicalAt DESC (head's posted_at) -
//     stable; reactions and replies update content in place but never
//     reorder rows.
//   - activity groups, filtered to hasRecentActivity and sorted by
//     lastActivityAt DESC - this is where bumping is allowed.
// Return the InboxRow inside the group whose lastActivityAt drove the
// group's lastActivityAt - i.e. the bundle that "caused" the activity
// bump. The activity panel renders this row (rather than the whole
// thread / chain) and uses its id to scroll the feed-side group to
// the matching message when the user clicks. Falls back to the head
// when nothing matches (safe default for groups whose head IS the
// activity).
export function activityRowFor(group: InboxGroup): InboxRow {
  const target = group.lastActivityAt;
  if (group.head.lastActivityAt === target) return group.head;
  for (const r of group.replies) {
    if (r.lastActivityAt === target) return r;
  }
  return group.head;
}

function hasRecentActivity(group: InboxGroup, now: number): boolean {
  // "Creation" includes every message in the head bundle - so a
  // burst of messages from the same author within the bundling
  // window doesn't read as activity beyond the conversation's own
  // start. Without this the bundle's lastActivityAt (newest message
  // in the burst) sits above chronologicalAt (oldest message in the
  // burst) and hasRecentActivity is true on what's really just one
  // logical post.
  let creationAt = group.chronologicalAt;
  for (const m of group.head.messages) {
    if (m.postedAt !== null && m.postedAt > creationAt) creationAt = m.postedAt;
  }
  // Polls are exempt from the "activity must be beyond creation" rule: a
  // poll being created / voted / closed is itself a feed-worthy event, so a
  // poll group qualifies on its event time alone (lastActivityAt === its
  // posted_at for a vote-less, open poll). lastActivityAt is built purely
  // from real event times now (see bundleToRow), so a backfilled poll still
  // carries an old timestamp and is excluded by the window check below.
  const isPoll = group.head.pollStatus !== null;
  if (!isPoll && group.lastActivityAt <= creationAt) return false;
  const ts = Date.parse(group.lastActivityAt);
  if (Number.isNaN(ts)) return false;
  return now - ts <= ACTIVITY_WINDOW_MS;
}

function groupRows(rows: InboxRow[]): InboxGroup[] {
  const inThreadGroup = new Set<string>();
  const groups: InboxGroup[] = [];

  // Global externalId index used to find a thread starter that lives
  // outside its thread's bucket (chat-spawned thread case).
  const rowByExternalId = new Map<string, InboxRow>();
  for (const r of rows) {
    const ext = r.messages[0]?.externalId;
    if (typeof ext === 'string') rowByExternalId.set(ext, r);
  }

  // Pass 1: thread groups (forum threads + chat-spawned threads).
  // Skip threads whose starter is a poll-bearing message - those
  // thread messages should render as their own top-level entries in
  // the message feed, with the poll itself in the polls feed. Otherwise
  // the thread folds under the poll group and is sorted by the poll's
  // posted_at, which buries fresh thread activity.
  const threadBuckets = new Map<string, InboxRow[]>();
  for (const r of rows) {
    if (r.threadId === null) continue;
    const starter = rowByExternalId.get(r.threadId);
    if (starter !== undefined && starter.pollStatus !== null) continue;
    let bucket = threadBuckets.get(r.threadId);
    if (bucket === undefined) {
      bucket = [];
      threadBuckets.set(r.threadId, bucket);
    }
    bucket.push(r);
    inThreadGroup.add(r.id);
  }

  for (const [threadId, bucket] of threadBuckets) {
    // Ascending sort is used to find the oldest in-bucket row (the
    // fallback head for starterMissing). The visible reply ordering
    // is set further down by lastActivityAt DESC, separately from
    // this scan.
    const orderedAsc = bucket
      .slice()
      .sort((a, b) =>
        (a.messages[0]?.postedAt ?? '').localeCompare(b.messages[0]?.postedAt ?? ''),
      );

    let head: InboxRow;
    let replies: InboxRow[];
    let starterMissing = false;
    let kind: GroupKind;

    const inBucketStarterIdx = orderedAsc.findIndex(
      (r) => r.messages[0]?.externalId === threadId,
    );
    if (inBucketStarterIdx !== -1) {
      // Forum post: starter and replies all share thread_id, and the
      // starter IS a thread message itself (not a chat message that
      // got threaded off).
      head = orderedAsc[inBucketStarterIdx]!;
      replies = orderedAsc.filter((_, i) => i !== inBucketStarterIdx);
      kind = 'forum-post';
    } else {
      const externalStarter = rowByExternalId.get(threadId);
      if (externalStarter !== undefined && !inThreadGroup.has(externalStarter.id)) {
        // Chat-spawned thread: parent message lives in a regular
        // channel with thread_id = null.
        head = externalStarter;
        replies = orderedAsc;
        inThreadGroup.add(externalStarter.id);
        kind = 'thread';
      } else {
        // Starter not on page yet. Default to forum-post styling -
        // chat-spawned thread parents are auto-fetched and resolved
        // in a later render, while forum starters older than the
        // page cursor are common for long-running threads.
        head = orderedAsc[0]!;
        replies = orderedAsc.slice(1);
        starterMissing = true;
        kind = 'forum-post';
      }
    }

    // Inside a thread, replies render newest-first by post time and
    // never reorder when reactions land - matching the chronological
    // feed's behaviour. Reactions on an older reply still update the
    // pill counts in place; only the position is stable.
    replies = replies
      .slice()
      .sort((a, b) =>
        (b.messages[0]?.postedAt ?? '').localeCompare(a.messages[0]?.postedAt ?? ''),
      );

    const lastActivityAt = [head, ...replies].reduce(
      (acc, r) => (r.lastActivityAt > acc ? r.lastActivityAt : acc),
      head.lastActivityAt,
    );
    groups.push({
      id: head.id,
      kind,
      head,
      replies,
      threadId,
      lastActivityAt,
      chronologicalAt: head.messages[0]?.postedAt ?? lastActivityAt,
      hasRecentActivity: false,
      starterMissing,
    });
  }

  // Pass 2: reply chains in regular channels. Build the parent index
  // from rows that didn't already get pulled into a thread group,
  // since a regular-channel chain can't have a thread parent.
  const remaining = rows.filter((r) => !inThreadGroup.has(r.id));
  const byExternalId = new Map<string, InboxRow>();
  for (const r of remaining) {
    const ext = r.messages[0]?.externalId;
    if (typeof ext === 'string') byExternalId.set(ext, r);
  }

  // Walk to the topmost on-page ancestor so reply-of-reply chains
  // collapse to a single flat replies list. Cycle guard via visited
  // set - replies in storage shouldn't form cycles, but defending
  // against a malformed import is cheap.
  function findTopAncestor(start: InboxRow): InboxRow | null {
    let current: InboxRow | undefined = start;
    const visited = new Set<string>();
    while (current !== undefined) {
      if (visited.has(current.id)) return null;
      visited.add(current.id);
      const parentExt = current.replyToExternalId;
      if (parentExt === null) return current;
      const parent = byExternalId.get(parentExt);
      if (parent === undefined) return null;
      current = parent;
    }
    return null;
  }

  // First identify which rows become reply children, so heads are the
  // remaining set. We only attach a child if its top ancestor differs
  // from itself - otherwise the row IS the top, and stays as a head.
  // Replies whose top ancestor is a poll-bearing message are kept as
  // their own top-level entry instead of nesting under the poll, so
  // the reply surfaces in the message feed at its own posted_at and
  // the poll keeps its place in the polls feed.
  const childOf = new Map<string, string>();
  for (const r of remaining) {
    if (r.replyToExternalId === null) continue;
    const top = findTopAncestor(r);
    if (top === null || top.id === r.id) continue;
    if (top.pollStatus !== null) continue;
    childOf.set(r.id, top.id);
  }

  const repliesByHead = new Map<string, InboxRow[]>();
  for (const [childId, headId] of childOf) {
    let list = repliesByHead.get(headId);
    if (list === undefined) {
      list = [];
      repliesByHead.set(headId, list);
    }
    const child = remaining.find((r) => r.id === childId);
    if (child !== undefined) list.push(child);
  }

  for (const r of remaining) {
    if (childOf.has(r.id)) continue;
    // Chronological ordering inside the chain (newest first by post
    // time). Reactions don't move the row; the pills update in place.
    const replies = (repliesByHead.get(r.id) ?? []).slice().sort((a, b) =>
      (b.messages[0]?.postedAt ?? '').localeCompare(a.messages[0]?.postedAt ?? ''),
    );
    const lastActivityAt = replies.reduce(
      (acc, x) => (x.lastActivityAt > acc ? x.lastActivityAt : acc),
      r.lastActivityAt,
    );
    groups.push({
      id: r.id,
      kind: replies.length === 0 ? 'single' : 'reply-chain',
      head: r,
      replies,
      threadId: null,
      lastActivityAt,
      chronologicalAt: r.messages[0]?.postedAt ?? lastActivityAt,
      hasRecentActivity: false,
      starterMissing: false,
    });
  }

  // Stamp hasRecentActivity once per group, then sort chronologically
  // for the feed. The activity-panel ordering is derived separately by
  // the hook so the same group objects are reused in both lists.
  const now = Date.now();
  for (const g of groups) {
    g.hasRecentActivity = hasRecentActivity(g, now);
  }
  groups.sort((a, b) => b.chronologicalAt.localeCompare(a.chronologicalAt));
  return groups;
}

const SELECT_COLUMNS =
  'id, author_handle, author_external_id, raw_text, posted_at, edited_at, signal_flags, attachments, ' +
  'discord_channel_id, discord_channel_name, discord_parent_channel_id, discord_parent_channel_name, ' +
  'thread_id, reply_to_external_id, external_id, ' +
  // Explicit FK hint: migration 0091 added feedback_pool.poll_id which
  // creates a second relationship to feedback_poll. We want the
  // feedback_poll row whose feedback_id points back to this message
  // (i.e. the poll attached to the row), not the cross-link from a
  // synthesised poll-source row. PostgREST disambiguates via !<fk col>.
  'feedback_poll!feedback_id(' +
    'id, question, answers, finalised, expires_at, updated_at, allow_multiselect, ' +
    'feedback_poll_vote(observed_at, answer_id, voter_external_id, action)' +
  '), ' +
  'feedback_reaction(emoji, action, observed_at, reactor_external_id)';

// Cap on the per-thread fetch (see thread-depth effect below). Most
// playtest threads will be well under this; the cap exists to bound the
// download cost of a runaway thread. If we ever hit it in the wild we
// can promote the thread fetch to a paginated path.
const THREAD_FETCH_CAP = 200;

// Stable key for the filter so we can use it as a useEffect dep without
// re-firing on identical-content object remounts.
function entityFilterKey(f: InboxEntityFilter | null): string {
  if (f === null) return 'all';
  if (f.kind === 'forum') return `forum:${f.name}`;
  return `${f.kind}:${f.channelId}`;
}

// Filter shape for the inbox. Threads and channels filter by
// discord_channel_id (what the bot stores per-message). Forums filter
// by discord_parent_channel_name because forum messages live inside
// threads - the forum's own snowflake never appears on a feedback row.
// Filtering by parent name relies on the denormalised column added in
// migration 0017.
export type InboxEntityFilter =
  | { kind: 'forum'; name: string }
  | { kind: 'channel' | 'thread'; channelId: string };

// The currently-watched entity set, used to scope the "All channels" view
// so historical rows from channels the studio no longer watches stop
// leaking into the feed and counts. Undefined = no scoping (back-compat
// for callers/tests that don't supply it); a provided object with empty
// arrays means "nothing watched" and yields an empty inbox.
export interface WatchedChannelScope {
  // Every watched entity's Discord snowflake (text/announcement channels,
  // threads, forums). Matches discord_channel_id, or discord_parent_channel_id
  // for thread children of a watched channel.
  channelIds: string[];
  // Watched forum names. Forum-thread rows denormalise the parent forum
  // name (migration 0017) but not reliably its snowflake, so forums match
  // on name - the same column the per-forum chip filter uses.
  forumParentNames: string[];
}

// PostgREST .or() group matching any watched entity. Returns null when the
// scope is empty (caller treats that as "show nothing"). Channel snowflakes
// are numeric and safe to inline; forum names are quoted + escaped so a
// comma / paren / quote in a name can't break the filter grammar.
function watchedScopeOrFilter(scope: WatchedChannelScope): string | null {
  const parts: string[] = [];
  if (scope.channelIds.length > 0) {
    const ids = scope.channelIds.join(',');
    parts.push(`discord_channel_id.in.(${ids})`);
    parts.push(`discord_parent_channel_id.in.(${ids})`);
  }
  if (scope.forumParentNames.length > 0) {
    const names = scope.forumParentNames
      .map((n) => `"${n.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
      .join(',');
    parts.push(`discord_parent_channel_name.in.(${names})`);
  }
  return parts.length > 0 ? parts.join(',') : null;
}

// Channel snowflakes a form-submission query should be scoped to for a given
// selection. null = unscoped (legacy). Forms never live in forum threads, so
// a forum chip yields an empty set (count 0).
function formScopeChannelIds(
  filter: InboxEntityFilter | null,
  scope: WatchedChannelScope | undefined,
): string[] | null {
  if (filter !== null) {
    return filter.kind === 'forum' ? [] : [filter.channelId];
  }
  return scope === undefined ? null : scope.channelIds;
}

export interface UseFeedbackInboxOptions {
  gameId: string;
  entityFilter: InboxEntityFilter | null;
  // Watched-entity set for scoping the "All channels" view. See
  // WatchedChannelScope. Omit to disable scoping.
  watchedScope?: WatchedChannelScope;
  // Whether the watched scope has finished resolving. The scope is built
  // from an async entity fetch, so it starts empty; an empty scope is
  // indistinguishable from "nothing watched", which would otherwise make
  // the hook fetch a throwaway empty page and report loading:false over
  // it. While this is false the hook stays in the loading state and skips
  // the fetch, so consumers never see a transient empty "loaded" feed
  // (which is what broke Insights deep-link resolution). Defaults to true
  // for callers/tests that don't supply a scope.
  scopeReady?: boolean;
  // Active game's build timeline + dev stages, sourced from shell context.
  // Used to surface "build changed" entries in the activity feed (and, in
  // PR3, divider rows in the feeds). Optional so existing callers/tests
  // that don't care about builds keep working - absent means no build
  // entries.
  buildMarkers?: BuildMarker[];
  devStages?: GameDevStage[];
}

export interface UseFeedbackInboxResult {
  // Top-level groups for the chronological feed - sorted by the head
  // row's posted_at DESC so the conversation order is preserved. New
  // reactions / replies update content in place but do not reorder.
  groups: InboxGroup[];
  // Subset of groups with activity beyond their own creation event
  // within ACTIVITY_WINDOW_MS, sorted by lastActivityAt DESC. Drives
  // the right-hand activity panel.
  activityGroups: InboxGroup[];
  // Bundled rows pre-grouping. Kept on the return shape because the
  // channel-chip derivation in the container needs the per-channel
  // count regardless of how rows ended up nested.
  rows: InboxRow[];
  // All parsed messages including signalFlags.isEmpty edges, for
  // reply-quote lookup by external_id (an empty image-only message
  // can still be a valid reply target).
  messages: InboxMessage[];
  // Poll-bearing groups for the polls feed. Fetched independently of
  // entityFilter so the polls feed can apply its own channel-chip
  // filter without reusing the message-feed scope. Each poll is its
  // own group of kind 'single' - polls don't bundle and we don't
  // try to thread/reply-chain them in the polls feed view.
  pollGroups: InboxGroup[];
  // Form submissions for the active game, newest first. Independent of
  // the message-feed entityFilter for the same reason as polls - the
  // submissions pane applies the page-level channel chip client-side
  // against each submission's surveyChannelId.
  formSubmissions: FormSubmissionEntry[];
  formSubmissionsHasMore: boolean;
  formSubmissionsLoadingMore: boolean;
  loadMoreFormSubmissions: () => Promise<void>;
  // Heterogeneous activity stream that merges chronological message
  // groups with hasRecentActivity AND form submissions submitted within
  // ACTIVITY_WINDOW_MS, sorted DESC by their respective activity
  // timestamps. Polls also flow through `groups` so they're already
  // covered by the group entries.
  activityEntries: ActivityEntry[];
  // Exact totals per feed type for the active channel selection (the
  // watched scope when "All channels" is active, otherwise the selected
  // chip). Server-side counts, so they reflect the true bucket size rather
  // than however many rows the paginated/bundled feeds currently hold.
  // messagesCount counts feedback records, not bundled conversation groups.
  messagesCount: number;
  pollsCount: number;
  formsCount: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
}

export function toFormSubmissionEntry(
  enriched: EnrichedFeedbackFormSubmission,
): FormSubmissionEntry {
  const { submission, form, fields, engagement } = enriched;
  const submittedAt = submission.submittedAt;
  const engagementTs = engagement?.lastActivityAt ?? null;
  const lastActivityAt =
    engagementTs !== null && engagementTs > submittedAt ? engagementTs : submittedAt;
  // A submission counts as "recent activity" if either the submission
  // itself or a later engagement event falls inside the activity window.
  const ageMs = Date.now() - new Date(lastActivityAt).getTime();
  return {
    id: submission.id,
    formId: submission.formId,
    feedbackPoolId: submission.feedbackPoolId,
    // Inbox cards display the portal label, not the Discord title.
    formTitle: form.name,
    surveyChannelId: form.surveyChannelId,
    submitterHandle: submission.submitterHandle,
    submitterExternalId: submission.submitterExternalId,
    submittedAt,
    values: submission.values,
    fields,
    engagement,
    lastActivityAt,
    hasRecentActivity: ageMs <= ACTIVITY_WINDOW_MS,
    // Filled in by mergeSubmissionSentiments once the page's sentiments
    // land; null until then so the card stays dot-free during the gap.
    sentimentBand: null,
  };
}

// Overlay derived sentiment bands onto a list of entries, keyed by
// submission id. Entries absent from the map keep their current band
// (null on first load). Returns a new array only when something changed
// so React can bail on an identical render.
function mergeSubmissionSentiments(
  entries: FormSubmissionEntry[],
  bands: Map<string, SentimentBand | null>,
): FormSubmissionEntry[] {
  let changed = false;
  const next = entries.map((entry) => {
    if (!bands.has(entry.id)) return entry;
    const band = bands.get(entry.id) ?? null;
    if (band === entry.sentimentBand) return entry;
    changed = true;
    return { ...entry, sentimentBand: band };
  });
  return changed ? next : entries;
}

export function useFeedbackInbox(opts: UseFeedbackInboxOptions): UseFeedbackInboxResult {
  const { gameId, entityFilter, watchedScope, scopeReady = true, buildMarkers, devStages } = opts;
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  // Poll-bearing rows for the polls feed - parallel state to
  // `messages` because the polls feed filters independently of the
  // message-feed entityFilter. Volumes are small (a studio rarely
  // has more than a few dozen polls live), so we don't paginate -
  // one fetch on mount + realtime updates is enough.
  const [pollMessages, setPollMessages] = useState<InboxMessage[]>([]);
  const [formSubmissions, setFormSubmissions] = useState<FormSubmissionEntry[]>(
    [],
  );
  const [formSubmissionsHasMore, setFormSubmissionsHasMore] =
    useState<boolean>(false);
  const [formSubmissionsLoadingMore, setFormSubmissionsLoadingMore] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Bundles are derived from the message list. Any state update -
  // initial load, pagination, Realtime refetch - flows through here.
  const rows = useMemo(() => bundleMessages(messages), [messages]);
  const groups = useMemo(() => groupRows(rows), [rows]);
  // Activity panel reuses the same group objects but applies a
  // different sort and filter. Deriving here (rather than inside
  // groupRows) keeps the chronological feed cheap and lets either
  // list re-derive without reshaping the whole grouping pass.
  const activityGroups = useMemo(
    () =>
      groups
        .filter((g) => g.hasRecentActivity)
        .slice()
        .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt)),
    [groups],
  );

  // Poll groups: each poll-bearing message becomes its own
  // single-kind group. Polls never bundle (shouldBundle bails on
  // poll presence), and we deliberately skip thread/reply-chain
  // grouping here so the polls feed shows one card per poll
  // regardless of where in a thread the poll sits.
  const pollGroups = useMemo<InboxGroup[]>(() => {
    const polls = pollMessages.filter((m) => m.poll !== null);
    const rowsForPolls = polls.map((m) => bundleToRow([m]));
    const out: InboxGroup[] = rowsForPolls.map((r) => ({
      id: r.id,
      kind: 'single' as const,
      head: r,
      replies: [],
      threadId: null,
      lastActivityAt: r.lastActivityAt,
      chronologicalAt: r.messages[0]?.postedAt ?? r.lastActivityAt,
      hasRecentActivity: false,
      starterMissing: false,
    }));
    const now = Date.now();
    for (const g of out) g.hasRecentActivity = hasRecentActivity(g, now);
    out.sort((a, b) => b.chronologicalAt.localeCompare(a.chronologicalAt));
    return out;
  }, [pollMessages]);

  // Stable string view of the filter for effect deps. The ref carries
  // the current filter object so callbacks can read it without forcing
  // every render to invalidate fetchPage / reload identity.
  const filterKey = useMemo(() => entityFilterKey(entityFilter), [entityFilter]);
  const entityFilterRef = useRef<InboxEntityFilter | null>(entityFilter);
  useEffect(() => {
    entityFilterRef.current = entityFilter;
  }, [entityFilter]);

  // Same ref+key pattern for the watched scope so the fetch callbacks read
  // the current scope without re-identifying on every render, and the
  // load effects re-run when the watched set changes.
  const scopeKey = useMemo(
    () =>
      watchedScope === undefined
        ? 'unscoped'
        : `${watchedScope.channelIds.join(',')}|${watchedScope.forumParentNames.join(',')}`,
    [watchedScope],
  );
  const watchedScopeRef = useRef<WatchedChannelScope | undefined>(watchedScope);
  useEffect(() => {
    watchedScopeRef.current = watchedScope;
  }, [watchedScope]);

  // Exact per-type counts for the active selection, refreshed below.
  const [messagesCount, setMessagesCount] = useState<number>(0);
  const [pollsCount, setPollsCount] = useState<number>(0);
  const [formsCount, setFormsCount] = useState<number>(0);

  // Tracks thread_ids we've already attempted a starter-fetch for so
  // the on-demand effect doesn't loop. Reset on reload (game switch
  // or filter change) so the new context gets fresh attempts.
  const attemptedStartersRef = useRef<Set<string>>(new Set());

  // Same idea, but for the thread-depth fetch: every time we see a
  // thread_id appear on the page we pull the thread's other messages
  // once. Pagination on the main feed is per-message, so a busy thread
  // can have replies older than the page cursor; without this, the
  // thread renders incomplete.
  const attemptedThreadDepthRef = useRef<Set<string>>(new Set());

  // Tracks whether the hook is still mounted. Async fetches that
  // resolve after unmount must skip their setState calls.
  const aliveRef = useRef<boolean>(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<InboxMessage[]> => {
      let query = supabase
        .from('feedback_pool')
        .select(SELECT_COLUMNS)
        .eq('game_id', gameId)
        .eq('source', 'discord_channel')
        // Replies / thread messages on a form-submission summary surface
        // under the parent in the form-submissions feed, not here.
        .is('form_engagement_submission_id', null)
        .order('posted_at', { ascending: false, nullsFirst: false })
        .limit(PAGE_SIZE);
      const filter = entityFilterRef.current;
      if (filter !== null) {
        if (filter.kind === 'forum') {
          query = query.eq('discord_parent_channel_name', filter.name);
        } else if (filter.kind === 'channel') {
          // A regular text/announcement channel chip should match its
          // own messages AND any messages in threads spawned from it.
          // Thread messages carry the thread's snowflake in
          // discord_channel_id and the parent's snowflake in
          // discord_parent_channel_id (migration 0020), so an OR over
          // both columns folds the thread children back into the chip.
          query = query.or(
            `discord_channel_id.eq.${filter.channelId},discord_parent_channel_id.eq.${filter.channelId}`,
          );
        } else {
          query = query.eq('discord_channel_id', filter.channelId);
        }
      } else {
        // "All channels": scope to the watched set so historical rows from
        // channels no longer watched don't leak in. No scope provided =
        // legacy game-wide load; empty scope = nothing watched -> no rows.
        const scope = watchedScopeRef.current;
        if (scope !== undefined) {
          const or = watchedScopeOrFilter(scope);
          if (or === null) return [];
          query = query.or(or);
        }
      }
      if (cursor !== null) query = query.lt('posted_at', cursor);

      const { data, error: err } = await query;
      if (err !== null) throw new Error(err.message);

      const parsed: InboxMessage[] = [];
      for (const raw of (data ?? []) as unknown[]) {
        const r = RowSchema.safeParse(raw);
        if (r.success) parsed.push(parseMessage(r.data));
      }
      return parsed;
    },
    [gameId, filterKey, scopeKey],
  );

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchPage(null);
      setMessages(page);
      setHasMore(page.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (loading) return;
    // Cursor is the oldest posted_at we currently hold. Bundles don't
    // affect this because lastActivityAt only governs display order.
    let oldestPostedAt: string | null = null;
    for (const m of messages) {
      if (m.postedAt === null) continue;
      if (oldestPostedAt === null || m.postedAt < oldestPostedAt) oldestPostedAt = m.postedAt;
    }
    if (oldestPostedAt === null) return;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchPage(oldestPostedAt);
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const fresh = page.filter((m) => !seen.has(m.id));
        return [...prev, ...fresh];
      });
      setHasMore(page.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more feedback');
    } finally {
      setLoading(false);
    }
  }, [fetchPage, loading, messages]);

  useEffect(() => {
    // A reload (filter change, game switch) replaces the message
    // set, so any starters / depth fetches we previously tried are
    // no longer relevant - clear the attempt caches so we'll try
    // again for the new context.
    attemptedStartersRef.current = new Set();
    attemptedThreadDepthRef.current = new Set();
    if (!scopeReady) {
      // Watched scope hasn't resolved yet. Hold in the loading state
      // instead of fetching against an unresolved (empty) scope - that
      // throwaway empty page is exactly what made deep-link resolution
      // give up early. We refetch once scopeReady flips true (the scope
      // populating also changes fetchPage identity, so reload re-fires).
      setLoading(true);
      return;
    }
    void reload();
  }, [reload, scopeReady]);

  // Polls feed fetch. Independent of entityFilter: the polls feed has
  // its own chip filter that applies client-side over this complete
  // set. !inner on the embed turns the feedback_poll join into a
  // semi-join so we only fetch poll-bearing rows. We don't paginate -
  // poll volumes are tiny next to message volumes; an outer cap keeps
  // a runaway studio from pulling everything.
  const POLL_FETCH_CAP = 500;
  const loadPolls = useCallback(async (): Promise<void> => {
    const SELECT_POLLS =
      'id, author_handle, author_external_id, raw_text, posted_at, edited_at, signal_flags, attachments, ' +
      'discord_channel_id, discord_channel_name, discord_parent_channel_id, discord_parent_channel_name, ' +
      'thread_id, reply_to_external_id, external_id, ' +
      // Same disambiguation as SELECT_COLUMNS above: pin to the
      // feedback_id FK so PostgREST doesn't trip over migration 0091's
      // feedback_pool.poll_id back-link.
      'feedback_poll!feedback_id!inner(' +
        'id, question, answers, finalised, expires_at, updated_at, allow_multiselect, ' +
        'feedback_poll_vote(observed_at, answer_id, voter_external_id, action)' +
      '), ' +
      'feedback_reaction(emoji, action, observed_at, reactor_external_id)';
    let query = supabase
      .from('feedback_pool')
      .select(SELECT_POLLS)
      .eq('game_id', gameId)
      .eq('source', 'discord_channel')
      .order('posted_at', { ascending: false, nullsFirst: false })
      .limit(POLL_FETCH_CAP);
    // Scope to the watched set for parity with the message feed. The polls
    // feed still applies its own per-chip filter client-side over this set.
    const scope = watchedScopeRef.current;
    if (scope !== undefined) {
      const or = watchedScopeOrFilter(scope);
      if (or === null) {
        if (aliveRef.current) setPollMessages([]);
        return;
      }
      query = query.or(or);
    }
    const { data, error: err } = await query;
    if (!aliveRef.current || err !== null || data === null) return;
    const parsed: InboxMessage[] = [];
    for (const raw of data as unknown[]) {
      const r = RowSchema.safeParse(raw);
      if (r.success) parsed.push(parseMessage(r.data));
    }
    setPollMessages(parsed);
  }, [gameId, scopeKey]);

  useEffect(() => {
    void loadPolls();
  }, [loadPolls]);

  // Form submissions fetch. Cursor-paginated newest-first; the inbox
  // shows the first page on mount and exposes loadMoreFormSubmissions
  // so the panel can fetch older pages on demand. Realtime keeps the
  // top of the list fresh independently of the cursor.
  const loadFormSubmissions = useCallback(async (): Promise<void> => {
    const scope = watchedScopeRef.current;
    // Nothing watched -> no submissions. (channelIds carries every watched
    // entity's snowflake; forms are scoped to their survey channel.)
    if (scope !== undefined && scope.channelIds.length === 0) {
      if (aliveRef.current) {
        setFormSubmissions([]);
        setFormSubmissionsHasMore(false);
      }
      return;
    }
    const enriched = await fetchFeedbackInboxSubmissions(gameId, {
      limit: INBOX_SUBMISSION_PAGE_SIZE,
      ...(scope !== undefined ? { channelIds: scope.channelIds } : {}),
    });
    if (!aliveRef.current) return;
    setFormSubmissions(enriched.map(toFormSubmissionEntry));
    setFormSubmissionsHasMore(enriched.length === INBOX_SUBMISSION_PAGE_SIZE);
  }, [gameId, scopeKey]);

  useEffect(() => {
    void loadFormSubmissions();
  }, [loadFormSubmissions]);

  const loadMoreFormSubmissions = useCallback(async (): Promise<void> => {
    if (formSubmissionsLoadingMore) return;
    if (!formSubmissionsHasMore) return;
    if (formSubmissions.length === 0) return;
    setFormSubmissionsLoadingMore(true);
    const cursor = formSubmissions[formSubmissions.length - 1]!.submittedAt;
    const scope = watchedScopeRef.current;
    const enriched = await fetchFeedbackInboxSubmissions(gameId, {
      limit: INBOX_SUBMISSION_PAGE_SIZE,
      before: cursor,
      ...(scope !== undefined ? { channelIds: scope.channelIds } : {}),
    });
    if (!aliveRef.current) {
      setFormSubmissionsLoadingMore(false);
      return;
    }
    setFormSubmissions((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const fresh = enriched
        .map(toFormSubmissionEntry)
        .filter((s) => !seen.has(s.id));
      return [...prev, ...fresh];
    });
    setFormSubmissionsHasMore(enriched.length === INBOX_SUBMISSION_PAGE_SIZE);
    setFormSubmissionsLoadingMore(false);
  }, [
    gameId,
    formSubmissions,
    formSubmissionsHasMore,
    formSubmissionsLoadingMore,
  ]);

  // Derived sentiment overlay. Whenever submissions land (initial load,
  // pagination, realtime insert) we fetch the sentiment band for any id we
  // haven't resolved yet in one batched RPC call and merge it in by id, so
  // cards show a band without an N+1 per card. requestedSentimentRef tracks
  // ids we've already asked about so a re-render doesn't refetch the page.
  const requestedSentimentRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    requestedSentimentRef.current = new Set();
  }, [gameId]);
  useEffect(() => {
    const pending = formSubmissions
      .filter((s) => s.sentimentBand === null)
      .map((s) => s.id)
      .filter((id) => !requestedSentimentRef.current.has(id));
    if (pending.length === 0) return;
    for (const id of pending) requestedSentimentRef.current.add(id);
    let cancelled = false;
    void (async () => {
      const sentiments = await fetchFormSubmissionSentiments(pending);
      if (cancelled || !aliveRef.current || sentiments.size === 0) return;
      const bands = new Map<string, SentimentBand | null>();
      for (const [id, s] of sentiments) bands.set(id, s.band);
      setFormSubmissions((prev) => mergeSubmissionSentiments(prev, bands));
    })();
    return () => {
      cancelled = true;
    };
  }, [formSubmissions]);

  // Form-submission realtime subscription. Self-heal pattern matches
  // the feedback_pool / feedback_inbox_read channels: visibility-state
  // rejoin + exponential backoff. We refetch the single inserted row
  // so the join (form metadata + field titles) is populated; bursts
  // coalesce on a 200ms timer so a multi-submission flurry hits the
  // network once.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const pendingIds = new Set<string>();

    async function flushPending(): Promise<void> {
      if (cancelled) return;
      const ids = [...pendingIds];
      pendingIds.clear();
      const fetched: EnrichedFeedbackFormSubmission[] = [];
      for (const id of ids) {
        const row = await fetchFeedbackFormSubmissionById(id);
        if (row !== null) fetched.push(row);
      }
      if (cancelled || fetched.length === 0) return;
      const scope = watchedScopeRef.current;
      setFormSubmissions((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        const fresh = fetched
          .map(toFormSubmissionEntry)
          .filter((s) => !seen.has(s.id))
          // Drop realtime inserts from channels outside the watched set so
          // the subscription can't reintroduce what the scoped load excludes.
          .filter(
            (s) =>
              scope === undefined ||
              (s.surveyChannelId !== null &&
                scope.channelIds.includes(s.surveyChannelId)),
          );
        if (fresh.length === 0) return prev;
        const next = [...fresh, ...prev];
        next.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
        return next;
      });
    }

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = 1_000;
    let lastStatus: string | null = null;

    function teardownChannel(): void {
      if (channel !== null) {
        void supabase.removeChannel(channel);
        channel = null;
      }
    }

    function connect(): void {
      if (cancelled) return;
      teardownChannel();
      const next = supabase
        .channel(`feedback-inbox-form-submissions:${gameId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feedback_form_submissions',
            filter: `game_id=eq.${gameId}`,
          },
          (payload) => {
            const parsed = FormSubmissionPayloadSchema.safeParse(payload.new);
            if (!parsed.success || cancelled) return;
            pendingIds.add(parsed.data.id);
            if (timer !== null) clearTimeout(timer);
            timer = setTimeout(() => {
              void flushPending();
            }, 200);
          },
        );
      channel = next;
      next.subscribe((status) => {
        if (cancelled) return;
        lastStatus = status;
        if (status === 'SUBSCRIBED') {
          reconnectDelay = 1_000;
          if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (reconnectTimer !== null) return;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      });
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (lastStatus !== 'SUBSCRIBED') {
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectDelay = 1_000;
        connect();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    connect();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      teardownChannel();
    };
  }, [gameId]);

  // Merge form submissions into the activity stream alongside groups
  // with recent activity. Both kinds carry their own lastActivityAt
  // (groups: head/reply timestamps + reaction observed_at; submissions:
  // submittedAt or latest engagement). Filter by hasRecentActivity so
  // the panel stays a triage list rather than a duplicate inbox.
  const activityEntries = useMemo<ActivityEntry[]>(() => {
    const entries: ActivityEntry[] = [];
    for (const g of activityGroups) {
      entries.push({ kind: 'group', group: g });
    }
    for (const s of formSubmissions) {
      if (!s.hasRecentActivity) continue;
      entries.push({ kind: 'form_submission', submission: s });
    }
    // Builds released within the activity window. Game-wide, so unlike
    // groups/submissions they're not subject to the channel chip - the
    // container leaves build entries in place when filtering.
    for (const change of buildChangesWithin(
      buildMarkers ?? [],
      devStages ?? [],
      Date.now(),
      ACTIVITY_WINDOW_MS,
    )) {
      entries.push({ kind: 'build_marker', marker: change.marker, changedAt: change.changedAt });
    }
    function timestampFor(e: ActivityEntry): string {
      if (e.kind === 'group') return e.group.lastActivityAt;
      if (e.kind === 'form_submission') return e.submission.lastActivityAt;
      return e.changedAt;
    }
    entries.sort((a, b) => timestampFor(b).localeCompare(timestampFor(a)));
    return entries;
  }, [activityGroups, formSubmissions, buildMarkers, devStages]);

  // Realtime subscriptions. The realtime payload from postgres_changes
  // carries the bare feedback_pool row but not the embedded poll /
  // reaction siblings, so we re-pull rows with the same SELECT to get
  // a fully-formed message back. A 200ms debounce on the feedback_pool
  // INSERT path coalesces bursts and gives the inserter a moment to
  // write feedback_poll after feedback_pool so the embed is populated
  // when we fetch.
  const firstPostedAtRef = useRef<string | null>(null);
  useEffect(() => {
    let max: string | null = null;
    for (const m of messages) {
      if (m.postedAt === null) continue;
      if (max === null || m.postedAt > max) max = m.postedAt;
    }
    firstPostedAtRef.current = max;
  }, [messages]);

  // Pull missing thread starters on demand. When a thread reply
  // lands but its parent message is older than the current page
  // cursor (or sits in a channel we didn't filter for), the parent
  // is absent from `messages` and the thread group renders headless.
  // After every state change we look for thread_ids whose starter
  // (a row with external_id == thread_id) isn't loaded, fetch those
  // rows in one batched .in() query, and merge them in. The
  // attemptedRef declared above guards against re-firing on every
  // render in the case the starter genuinely doesn't exist (e.g.
  // permissions).
  //
  // Cancellation: we deliberately do NOT abort the in-flight fetch
  // when the effect re-runs (e.g. because `messages` updated between
  // firing and resolving). If we did, the very setMessages call that
  // triggered re-run would also abort the orphan-resolving fetch,
  // and the page would be stuck in starterMissing mode until a full
  // remount. The aliveRef guards against setting state after the
  // hook has unmounted, which is the only race we actually need to
  // protect against.
  useEffect(() => {
    const externalIds = new Set<string>();
    for (const m of messages) {
      if (m.externalId !== null) externalIds.add(m.externalId);
    }
    const missing = new Set<string>();
    for (const m of messages) {
      if (m.threadId === null) continue;
      if (externalIds.has(m.threadId)) continue;
      if (attemptedStartersRef.current.has(m.threadId)) continue;
      missing.add(m.threadId);
    }
    if (missing.size === 0) return;

    const ids = [...missing];
    for (const id of ids) attemptedStartersRef.current.add(id);

    void (async () => {
      const { data, error: err } = await supabase
        .from('feedback_pool')
        .select(SELECT_COLUMNS)
        .eq('game_id', gameId)
        .in('external_id', ids);
      if (!aliveRef.current || err !== null || data === null) return;
      const parsed: InboxMessage[] = [];
      for (const raw of data as unknown[]) {
        const r = RowSchema.safeParse(raw);
        if (r.success) parsed.push(parseMessage(r.data));
      }
      if (parsed.length === 0) return;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const fresh = parsed.filter((m) => !seen.has(m.id));
        if (fresh.length === 0) return prev;
        return [...prev, ...fresh];
      });
    })();
  }, [messages, gameId]);

  // Thread-depth backfill. The main feed pages by posted_at across
  // every channel, so a thread whose replies are older than the page
  // cursor renders incomplete - exactly the symptom the inbox shows
  // for a chatty thread that's been live for a while. For each
  // thread_id present on the page, we pull up to THREAD_FETCH_CAP
  // messages with that thread_id and merge them in. attempted set
  // prevents a re-fire loop; reload() clears it so a filter / game
  // switch fetches afresh.
  useEffect(() => {
    const threadIds = new Set<string>();
    for (const m of messages) {
      if (m.threadId === null) continue;
      if (attemptedThreadDepthRef.current.has(m.threadId)) continue;
      threadIds.add(m.threadId);
    }
    if (threadIds.size === 0) return;
    const ids = [...threadIds];
    for (const id of ids) attemptedThreadDepthRef.current.add(id);

    void (async () => {
      const { data, error: err } = await supabase
        .from('feedback_pool')
        .select(SELECT_COLUMNS)
        .eq('game_id', gameId)
        .eq('source', 'discord_channel')
        .is('form_engagement_submission_id', null)
        .in('thread_id', ids)
        .order('posted_at', { ascending: false, nullsFirst: false })
        .limit(THREAD_FETCH_CAP);
      if (!aliveRef.current || err !== null || data === null) return;
      const parsed: InboxMessage[] = [];
      for (const raw of data as unknown[]) {
        const r = RowSchema.safeParse(raw);
        if (r.success) parsed.push(parseMessage(r.data));
      }
      if (parsed.length === 0) return;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const fresh = parsed.filter((m) => !seen.has(m.id));
        if (fresh.length === 0) return prev;
        return [...prev, ...fresh];
      });
    })();
  }, [messages, gameId]);

  // Refetch a single message when a reaction / poll-vote / poll-update
  // event arrives. Replacing the message in state triggers re-bundling
  // via useMemo: the bundle's lastActivityAt picks up the fresh
  // observed_at, which only matters for the activity panel's ordering.
  // The chronological feed sorts by chronologicalAt and keeps the row
  // in place. Updates the polls-feed list in parallel so a poll vote /
  // finalisation surfaces in both panels without a second roundtrip.
  const refetchMessage = useCallback(async (feedbackId: string): Promise<void> => {
    const { data, error: err } = await supabase
      .from('feedback_pool')
      .select(SELECT_COLUMNS)
      .eq('id', feedbackId)
      .maybeSingle();
    if (err !== null || data === null) return;
    const parsed = RowSchema.safeParse(data);
    if (!parsed.success) return;
    const fresh = parseMessage(parsed.data);
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === feedbackId);
      if (idx === -1) return prev;
      const next = prev.slice();
      next[idx] = fresh;
      return next;
    });
    setPollMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === feedbackId);
      if (idx === -1) {
        // New poll arrived (or one we hadn't yet pulled): if the fresh
        // row carries a poll, fold it in so the polls feed sees it
        // without waiting for the next reload.
        if (fresh.poll === null) return prev;
        return [fresh, ...prev];
      }
      const next = prev.slice();
      next[idx] = fresh;
      return next;
    });
  }, []);

  // Map a poll_id arriving on a feedback_poll_vote INSERT back to the
  // carrying feedback_pool row id so we know which message to refetch.
  // The pollMessages list is the authoritative source - the polls feed
  // is the one that always carries every active poll, while the
  // message-feed list may have filtered the parent out.
  const refetchByPollId = useCallback(
    (pollId: string): void => {
      let feedbackId: string | null = null;
      for (const m of pollMessages) {
        if (m.pollId === pollId) {
          feedbackId = m.id;
          break;
        }
      }
      if (feedbackId === null) {
        for (const m of messages) {
          if (m.pollId === pollId) {
            feedbackId = m.id;
            break;
          }
        }
      }
      if (feedbackId !== null) void refetchMessage(feedbackId);
    },
    [pollMessages, messages, refetchMessage],
  );

  // A feedback_reaction (or thread / reply landing as a discord_channel
  // pool row) can target a form submission's sibling pool row. The
  // reaction subscription only knows the feedback_id; scan loaded
  // submissions for a match, re-pull the submission's engagement, and
  // splice the fresh entry in place so the activity feed re-sorts. Use
  // a ref so the callback identity stays stable across submission
  // updates - otherwise the realtime channel would tear down and
  // reconnect every time a new submission landed.
  const formSubmissionsRef = useRef<FormSubmissionEntry[]>([]);
  useEffect(() => {
    formSubmissionsRef.current = formSubmissions;
  }, [formSubmissions]);
  const refetchFormSubmissionByPoolId = useCallback(
    async (feedbackPoolId: string): Promise<void> => {
      const target = formSubmissionsRef.current.find(
        (s) => s.feedbackPoolId === feedbackPoolId,
      );
      if (target === undefined) return;
      const fresh = await fetchFeedbackFormSubmissionById(target.id);
      if (fresh === null) return;
      const entry = toFormSubmissionEntry(fresh);
      setFormSubmissions((prev) => {
        const idx = prev.findIndex((s) => s.id === entry.id);
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = entry;
        return next;
      });
    },
    [],
  );

  // Replies / thread messages on a form summary land in feedback_pool
  // with source = 'discord_channel' and a non-null reply_to_external_id
  // or thread_id pointing at the summary's snowflake. The reaction
  // handler above covers reactions; this one covers replies + threads.
  const refetchFormSubmissionByExternalId = useCallback(
    async (externalId: string): Promise<void> => {
      const target = formSubmissionsRef.current.find(
        (s) => s.engagement?.summaryExternalId === externalId,
      );
      if (target === undefined) return;
      const fresh = await fetchFeedbackFormSubmissionById(target.id);
      if (fresh === null) return;
      const entry = toFormSubmissionEntry(fresh);
      setFormSubmissions((prev) => {
        const idx = prev.findIndex((s) => s.id === entry.id);
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = entry;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function fetchFresh(): Promise<void> {
      const cursor = firstPostedAtRef.current;
      const filter = entityFilterRef.current;
      let query = supabase
        .from('feedback_pool')
        .select(SELECT_COLUMNS)
        .eq('game_id', gameId)
        .eq('source', 'discord_channel')
        .is('form_engagement_submission_id', null)
        .order('posted_at', { ascending: false, nullsFirst: false })
        .limit(PAGE_SIZE);
      if (filter !== null) {
        if (filter.kind === 'forum') {
          query = query.eq('discord_parent_channel_name', filter.name);
        } else if (filter.kind === 'channel') {
          query = query.or(
            `discord_channel_id.eq.${filter.channelId},discord_parent_channel_id.eq.${filter.channelId}`,
          );
        } else {
          query = query.eq('discord_channel_id', filter.channelId);
        }
      }
      if (cursor !== null) query = query.gt('posted_at', cursor);

      const { data, error: err } = await query;
      if (cancelled || err !== null) return;

      const parsed: InboxMessage[] = [];
      for (const raw of (data ?? []) as unknown[]) {
        const r = RowSchema.safeParse(raw);
        if (r.success) parsed.push(parseMessage(r.data));
      }
      if (parsed.length === 0) return;

      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const fresh = parsed.filter((m) => !seen.has(m.id));
        if (fresh.length === 0) return prev;
        return [...fresh, ...prev];
      });
    }

    // Self-healing realtime subscription.
    //
    // supabase-js auto-reconnects the underlying WS, but a channel that
    // ends up in CLOSED / CHANNEL_ERROR / TIMED_OUT state doesn't always
    // rejoin on its own - particularly after long idle periods, laptop
    // sleep, or backgrounded tabs. We saw this locally as repeated
    // CLOSED -> SUBSCRIBED -> CLOSED transitions where the second close
    // never recovered. The wrapper below tears the channel down and
    // recreates a fresh one with exponential backoff on bad status, and
    // also forces a fresh connect when the tab becomes visible again.
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = 1_000;
    let lastStatus: string | null = null;

    function teardownChannel(): void {
      if (channel !== null) {
        void channel.unsubscribe();
        channel = null;
      }
    }

    function connect(): void {
      if (cancelled) return;
      teardownChannel();
      const next = supabase
        .channel(`feedback-inbox:${gameId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feedback_pool',
            filter: `game_id=eq.${gameId}`,
          },
          (payload) => {
            // Fan-out: a feedback_pool INSERT that points back at one of
            // the loaded submissions' summary messages should refresh
            // that submission's engagement so replies + thread bodies
            // appear live alongside reactions. The check runs ahead of
            // the debounce so we don't lose payload identity once the
            // timer fires.
            const parsed = FeedbackPoolInsertPayloadSchema.safeParse(payload.new);
            if (parsed.success) {
              const replyParent = parsed.data.reply_to_external_id ?? null;
              const threadParent = parsed.data.thread_id ?? null;
              if (replyParent !== null) {
                void refetchFormSubmissionByExternalId(replyParent);
              }
              if (threadParent !== null && threadParent !== replyParent) {
                void refetchFormSubmissionByExternalId(threadParent);
              }
            }
            if (timer !== null) clearTimeout(timer);
            timer = setTimeout(() => {
              if (cancelled) return;
              void fetchFresh();
              // A feedback_pool INSERT might be a brand new poll the
              // polls feed should pick up too. loadPolls is idempotent
              // (full refetch into setPollMessages), so coalescing it
              // on the same 200ms debounce is cheap.
              void loadPolls();
            }, 200);
          },
        )
        // feedback_reaction has no game_id column; we subscribe broadly
        // and rely on RLS plus the in-state lookup in refetchMessage to
        // ignore reactions for messages we don't currently render.
        // The same INSERT can target a form submission's sibling pool
        // row, so we fan the event out to both refetchers - whichever
        // path doesn't match its parent silently bails.
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'feedback_reaction' },
          (payload) => {
            const parsed = ReactionPayloadSchema.safeParse(payload.new);
            if (!parsed.success || cancelled) return;
            void refetchMessage(parsed.data.feedback_id);
            void refetchFormSubmissionByPoolId(parsed.data.feedback_id);
          },
        )
        // Poll vote events. The payload carries poll_id; we map that
        // back to a feedback_id via the in-state poll list and refetch
        // the carrier message so the poll's vote tally + lastVoteAt
        // refresh in both feeds.
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'feedback_poll_vote' },
          (payload) => {
            const parsed = PollVotePayloadSchema.safeParse(payload.new);
            if (!parsed.success || cancelled) return;
            refetchByPollId(parsed.data.poll_id);
          },
        )
        // Poll-state UPDATE events (vote tallies on answers, finalised
        // flips, expires_at adjustments) ride feedback_poll directly -
        // the bot's MESSAGE_UPDATE handler writes here. Migration 0022
        // adds this table to supabase_realtime with REPLICA IDENTITY
        // FULL so the new row is in the payload.
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'feedback_poll' },
          (payload) => {
            const parsed = PollUpdatePayloadSchema.safeParse(payload.new);
            if (!parsed.success || cancelled) return;
            void refetchMessage(parsed.data.feedback_id);
          },
        );
      channel = next;
      next.subscribe((status, err) => {
        // Surface subscription state in the console while we settle the
        // recovery behaviour - SUBSCRIBED = healthy; the rest are
        // transient or terminal failure modes the wrapper retries.
        // eslint-disable-next-line no-console
        console.log('[feedback-inbox] channel status', status, err ?? null);
        if (cancelled) return;
        lastStatus = status;
        if (status === 'SUBSCRIBED') {
          reconnectDelay = 1_000;
          if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (reconnectTimer !== null) return;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      });
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      // If we left the tab while healthy, the WS may have been frozen
      // and the channel may now be on its way to CLOSED without us
      // having seen the status change yet. Force a clean reconnect on
      // visible rather than waiting for the eventual error.
      if (lastStatus !== 'SUBSCRIBED') {
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectDelay = 1_000;
        connect();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    connect();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      teardownChannel();
    };
  }, [
    gameId,
    refetchMessage,
    refetchByPollId,
    refetchFormSubmissionByPoolId,
    refetchFormSubmissionByExternalId,
    loadPolls,
  ]);

  // Exact per-type counts for the active selection. Independent of the
  // paginated/bundled display state so the tab chips show true totals that
  // reconcile between "All channels" and the individual channel chips.
  // Re-runs when the game, chip selection, or watched scope changes.
  useEffect(() => {
    let cancelled = false;
    const filter = entityFilterRef.current;
    const scope = watchedScopeRef.current;

    // Resolve the active channel selection into a single descriptor the
    // feedback_pool count queries can apply. 'empty' = "All channels" with
    // nothing watched (report 0 without a round-trip); 'none' = legacy
    // unscoped all.
    const selection: { mode: 'none' | 'empty' }
      | { mode: 'eq'; col: string; val: string }
      | { mode: 'or'; expr: string } = (() => {
      if (filter !== null) {
        if (filter.kind === 'forum') {
          return { mode: 'eq' as const, col: 'discord_parent_channel_name', val: filter.name };
        }
        if (filter.kind === 'channel') {
          return {
            mode: 'or' as const,
            expr: `discord_channel_id.eq.${filter.channelId},discord_parent_channel_id.eq.${filter.channelId}`,
          };
        }
        return { mode: 'eq' as const, col: 'discord_channel_id', val: filter.channelId };
      }
      if (scope === undefined) return { mode: 'none' as const };
      const or = watchedScopeOrFilter(scope);
      return or === null ? { mode: 'empty' as const } : { mode: 'or' as const, expr: or };
    })();

    void (async () => {
      // Messages: feedback records (not bundled groups), excluding the
      // replies that surface under the form-submissions feed.
      let mCount = 0;
      if (selection.mode !== 'empty') {
        let mq = supabase
          .from('feedback_pool')
          .select('id', { count: 'exact', head: true })
          .eq('game_id', gameId)
          .eq('source', 'discord_channel')
          .is('form_engagement_submission_id', null);
        if (selection.mode === 'eq') mq = mq.eq(selection.col, selection.val);
        else if (selection.mode === 'or') mq = mq.or(selection.expr);
        mCount = (await mq).count ?? 0;
      }

      // Polls: poll-bearing records (inner join to feedback_poll).
      let pCount = 0;
      if (selection.mode !== 'empty') {
        let pq = supabase
          .from('feedback_pool')
          .select('id, feedback_poll!feedback_id!inner(id)', { count: 'exact', head: true })
          .eq('game_id', gameId)
          .eq('source', 'discord_channel');
        if (selection.mode === 'eq') pq = pq.eq(selection.col, selection.val);
        else if (selection.mode === 'or') pq = pq.or(selection.expr);
        pCount = (await pq).count ?? 0;
      }

      // Forms: submissions whose form's survey channel is in the selection.
      const formIds = formScopeChannelIds(filter, scope);
      let fCount = 0;
      if (formIds === null || formIds.length > 0) {
        let fq = supabase
          .from('feedback_form_submissions')
          .select('id, feedback_forms!inner(survey_channel_id)', { count: 'exact', head: true })
          .eq('game_id', gameId);
        if (formIds !== null) fq = fq.in('feedback_forms.survey_channel_id', formIds);
        fCount = (await fq).count ?? 0;
      }

      if (cancelled || !aliveRef.current) return;
      setMessagesCount(mCount);
      setPollsCount(pCount);
      setFormsCount(fCount);
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId, filterKey, scopeKey]);

  return {
    groups,
    activityGroups,
    pollGroups,
    formSubmissions,
    formSubmissionsHasMore,
    formSubmissionsLoadingMore,
    loadMoreFormSubmissions,
    activityEntries,
    messagesCount,
    pollsCount,
    formsCount,
    rows,
    messages,
    loading,
    error,
    hasMore,
    loadMore,
    reload,
  };
}
